import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Search, X } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { searchProductCategories } from '@/hooks/useInventory';
import { cn } from '@/lib/utils';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Product-category picker. Opens a dialog with a debounced typeahead against
 * the shared `search-product-categories` API. Picks bubble up as
 * `{ id, title, fullTree }`; clearing bubbles `null`.
 *
 * The save still goes via the existing `editProduct` (PUT
 * /api/inventory/{shopId}/{barcode}) — see ProductDetail's onSave. Behind
 * the scenes that writes a custom-product row with `source: 'myshop'`, so the
 * retailer's choice survives subsequent automated enrichment runs.
 */
export function CategoryPicker({ value, onChange }) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const inputRef = useRef(null);

	const runSearch = useCallback(async (q) => {
		setSearching(true);
		try {
			const data = await searchProductCategories(q);
			setResults(Array.isArray(data) ? data : []);
		} finally {
			setSearching(false);
		}
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		const handle = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [open, query, runSearch]);

	useEffect(() => {
		if (open) {
			setQuery('');
			setResults([]);
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [open]);

	const handlePick = (category) => {
		onChange({ id: category.id, title: category.title, fullTree: category.full_tree });
		setOpen(false);
	};

	const handleClear = (e) => {
		e.stopPropagation();
		onChange(null);
	};

	const label = value?.title || 'Uncategorised';

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-border bg-white px-3.5 text-sm text-foreground transition hover:bg-accent focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
			>
				<span className={cn('truncate', !value && 'text-muted-foreground')}>{label}</span>
				<span className="flex shrink-0 items-center gap-2">
					{value ? (
						<span
							onClick={handleClear}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleClear(e);
								}
							}}
							className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
							aria-label="Clear category"
							role="button"
							tabIndex={0}
						>
							<X className="h-3.5 w-3.5" />
						</span>
					) : null}
					<ChevronRight className="h-4 w-4 rotate-90 text-muted-foreground" />
				</span>
			</button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>Pick a product category</DialogTitle>
						<DialogDescription>Search Google&apos;s product taxonomy and pick the closest match.</DialogDescription>
					</DialogHeader>
					<div className="min-w-0 space-y-3">
						<div className="relative">
							<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<input
								ref={inputRef}
								type="text"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="e.g. Pet food, Coffee, T-shirt"
								className="h-11 w-full rounded-lg border border-border bg-white pl-10 pr-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
							/>
						</div>
						<div className="max-h-72 overflow-y-auto rounded-lg border border-border">
							{searching ? (
								<div className="p-3 text-sm text-muted-foreground">Searching…</div>
							) : results.length === 0 ? (
								<div className="p-3 text-sm text-muted-foreground">
									{query ? 'No matches.' : 'Start typing to search the taxonomy.'}
								</div>
							) : (
								<ul className="divide-y divide-border">
									{results.map((c) => (
										<li key={c.id}>
											<button
												type="button"
												onClick={() => handlePick(c)}
												className="block w-full px-3 py-2.5 text-left text-sm transition hover:bg-accent"
											>
												<div className="break-words font-semibold text-foreground">{c.title}</div>
												{c.full_tree ? (
													<div className="mt-0.5 break-words text-xs text-muted-foreground">{c.full_tree}</div>
												) : null}
											</button>
										</li>
									))}
								</ul>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
